import { effect, type ReadonlySignal } from '@preact/signals-core';

export const viewIdExists = (
  viewIds$: ReadonlySignal<readonly string[]>,
  viewId: string
): boolean => viewIds$.value.includes(viewId);

export const closeOnViewIdRemoval = (
  viewIds$: ReadonlySignal<readonly string[]>,
  viewId: string,
  close: () => void
): (() => void) => {
  let active = true;
  let stop: (() => void) | undefined;
  const dispose = () => {
    if (!active) {
      return;
    }
    active = false;
    stop?.();
  };
  stop = effect(() => {
    if (active && !viewIdExists(viewIds$, viewId)) {
      active = false;
      stop?.();
      close();
    }
  });
  if (!active) {
    stop();
  }
  return dispose;
};

export const createViewPopupLifetime = (onEmpty: () => void) => {
  const closeCallbacks = new Set<() => void>();
  let closing = false;
  let finished = false;

  const finishIfEmpty = () => {
    if (!closing && !finished && closeCallbacks.size === 0) {
      finished = true;
      onEmpty();
    }
  };

  return {
    close: () => {
      if (finished) {
        return;
      }
      closing = true;
      while (closeCallbacks.size > 0) {
        const close = [...closeCallbacks].at(-1);
        if (!close) {
          break;
        }
        close();
        closeCallbacks.delete(close);
      }
      closing = false;
      finishIfEmpty();
    },
    register: (close: () => void) => {
      if (closing || finished) {
        close();
        return () => {};
      }
      closeCallbacks.add(close);
      return () => {
        closeCallbacks.delete(close);
        finishIfEmpty();
      };
    },
  };
};
